"use client";

import { useUser } from "@clerk/nextjs";
import { usePathname } from "next/navigation";
import { type ReactNode, useEffect, useRef } from "react";
import {
  boot as bootIntercom,
  load as loadIntercom,
  shutdown as shutdownIntercom,
  update as updateIntercom,
} from "./Intercom";

type IntercomOptions = {
  user_id?: string;
  email?: string;
  name?: string;
  DiyerProfile?: boolean;
  TradieProfile?: boolean;
  "Tradie Profile"?: boolean;
  "Diyer Profile"?: boolean;
  TeletradieDiyerProfile?: boolean;
  TeletradieTradieProfile?: boolean;
  PermissionRoles?: string[];
  created_at?: number;
  custom_metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

type IntercomProviderProps = {
  children: ReactNode;
  additionalOptions?: Record<string, unknown>;
};

export const IntercomProvider = ({
  children,
  additionalOptions = {},
}: IntercomProviderProps) => {
  const _pathname = usePathname();
  const { user, isLoaded } = useUser();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize Intercom when component mounts and user data is loaded
  useEffect(() => {
    if (typeof window !== "undefined" && isLoaded) {
      loadIntercom();

      // Prepare user options for authenticated users
      const userOptions: IntercomOptions = {
        ...additionalOptions,
      };

      // If user is authenticated, add their information
      if (user) {
        userOptions.user_id = user.id;
        const email = user.primaryEmailAddress?.emailAddress;
        if (email !== undefined) {
          userOptions.email = email;
        }
        // Add DiyerProfile and TradieProfile boolean attributes based on user roles
        const userRoles = user.publicMetadata.roles as string[] | undefined;
        const teletradieRoles = user.publicMetadata.teletradieRoles as
          | string[]
          | undefined;
        const permissionRoles = user.publicMetadata.permissionRoles as
          | string[]
          | undefined;

        if (userRoles) {
          userOptions.DiyerProfile = userRoles.includes("diyer");
          userOptions.TradieProfile = userRoles.includes("tradie");
          userOptions["Tradie Profile"] = userRoles.includes("tradie");
          userOptions["Diyer Profile"] = userRoles.includes("diyer");
        }

        // Add teletradie roles as separate attributes
        if (teletradieRoles) {
          userOptions.TeletradieDiyerProfile =
            teletradieRoles.includes("diyer");
          userOptions.TeletradieTradieProfile =
            teletradieRoles.includes("tradie");
        }

        // Add permission roles
        if (permissionRoles) {
          userOptions.PermissionRoles = permissionRoles;
        }
        userOptions.name =
          user.fullName ||
          `${user.firstName || ""} ${user.lastName || ""}`.trim();

        // Add creation date if available
        if (user.createdAt) {
          userOptions.created_at = Math.floor(user.createdAt.getTime() / 1000);
        }

        // Add any custom metadata
        if (user.publicMetadata) {
          userOptions.custom_metadata = user.publicMetadata;
        }
      }

      bootIntercom(userOptions);
    }
  }, [isLoaded, user, additionalOptions]);

  // Update Intercom when pathname changes (route navigation)
  useEffect(() => {
    if (typeof window !== "undefined" && isLoaded) {
      updateIntercom();
    }
  }, [isLoaded]);

  // Handle user logout - shutdown Intercom to clear session
  useEffect(() => {
    if (isLoaded && !user) {
      // User logged out, shutdown Intercom and restart for anonymous users
      shutdownIntercom();
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        bootIntercom(additionalOptions);
        timeoutRef.current = null;
      }, 100);
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [user, isLoaded, additionalOptions]);

  return <>{children}</>;
};
